'use client'

import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'

import { talentPoolCopy } from '@/app/talent-pool/copy'

export function TalentPoolFaqSection() {
  const copy = talentPoolCopy
  const [expandedItems, setExpandedItems] = useState<string[]>([])

  const toggleItem = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    )
  }

  return (
    <section
      id="faq"
      className="pb-14"
    >
      <div className="rounded-3xl border border-border-primary bg-white dark:bg-card px-5 py-10 sm:px-8">
        <div className="mx-auto flex w-full max-w-[664px] flex-col items-center gap-4 text-center">
          <div className="rounded-full border-[1.5px] border-surface-primary bg-white dark:bg-card px-3 py-2 text-sm text-text-body">
            <HelpCircle className="mr-1.5 inline-block h-3.5 w-3.5" />
            {copy.landing.faqTitle}
          </div>
          <h2 className="text-[32px] font-medium leading-[40px] tracking-[-0.02em] text-text-heading md:text-[48px] md:leading-[60px] md:tracking-[-0.04em]">
            {copy.landing.faqTitle}
          </h2>
        </div>

        <div className="mx-auto mt-10 max-w-3xl space-y-4">
          {copy.landing.faqItems.map((faq, index) => {
            const faqId = `talent-pool-faq-${index}`
            const isExpanded = expandedItems.includes(faqId)

            return (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
              >
                <Card className="overflow-hidden rounded-2xl border border-border-primary bg-white dark:bg-card transition hover:border-brand-primary/20">
                  <button
                    type="button"
                    onClick={() => toggleItem(faqId)}
                    className="w-full text-left"
                    aria-expanded={isExpanded}
                  >
                    <CardContent className="flex flex-col gap-4 p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex flex-1 items-start gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border-primary bg-surface-primary text-brand-accent">
                            <HelpCircle className="h-5 w-5" />
                          </div>
                          <h3 className="text-lg font-semibold text-text-heading">
                            {faq.question}
                          </h3>
                        </div>
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border-primary bg-surface-primary text-text-muted">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {isExpanded ? (
                          <motion.div
                            key="answer"
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.2 }}
                            className="pl-12 text-sm leading-relaxed text-text-muted md:text-base"
                          >
                            {faq.answer}
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </CardContent>
                  </button>
                </Card>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
